import { firstValueFrom } from 'rxjs';
import { ScholarshipApiService } from '../../../../services/api/scholarship-api.service';
import { ToastService } from '../../services/toast.service';
import { Component, OnDestroy, EventEmitter, HostListener, Input, Output, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { MediaItem } from '../../../../models/scholarship.model';
import { ModalComponent } from '../modal/modal.component';

@Component({
  selector: 'app-document-list',
  standalone: true,
  imports: [CommonModule, ModalComponent],
  templateUrl: './document-list.component.html',
  styleUrl: './document-list.component.scss',
})
export class DocumentListComponent implements OnDestroy {
  private api = inject(ScholarshipApiService);
  private toast = inject(ToastService);
  private objectUrl: string | null = null;
  private sanitizer = inject(DomSanitizer);

  @Input() documents: MediaItem[] = [];
  @Input() showDelete = false;
  @Output() delete = new EventEmitter<number>();

  previewMedia = signal<MediaItem | null>(null);
  safePreviewUrl: SafeResourceUrl | null = null;

  @HostListener('window:keydown.escape')
  handleEscapeKey(): void {
    if (this.previewMedia()) {
      this.closePreview();
    }
  }

  async openPreview(media: MediaItem): Promise<void> {
    this.closePreview();
    this.previewMedia.set({ ...media, url: '' });
    try {
      const blob = await firstValueFrom(this.api.downloadMedia(media.id));
      if (this.previewMedia()?.id !== media.id) return;
      this.objectUrl = URL.createObjectURL(blob);
      this.previewMedia.set({ ...media, url: this.objectUrl });
      this.safePreviewUrl = this.getFileType(media.fileName) === 'pdf'
        ? this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl) : null;
    } catch {
      this.closePreview();
      this.toast.show('Não foi possível abrir o documento. Verifique seu acesso e tente novamente.', 'error');
    }
  }

  async download(media: MediaItem): Promise<void> {
    try {
      const blob = await firstValueFrom(this.api.downloadMedia(media.id));
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a'); link.href = url; link.download = media.fileName;
      link.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { this.toast.show('Não foi possível baixar o documento.', 'error'); }
  }

  closePreview(): void {
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = null;
    this.previewMedia.set(null);
    this.safePreviewUrl = null;
  }
  ngOnDestroy(): void { this.closePreview(); }

  getMediaUrl(url: string): string {
    if (!url) return '';
    if (url.startsWith('/v1/media/proxy/')) {
      return `/api${url}`;
    }
    return url;
  }

  getFileSize(size?: number): string {
    if (!size) return '—';
    if (size === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(size) / Math.log(k));
    return parseFloat((size / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  getFileType(fileName: string): string {
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return 'pdf';
      case 'jpg':
      case 'jpeg':
      case 'png': return 'image';
      case 'doc':
      case 'docx': return 'word';
      default: return 'text';
    }
  }

  getFileTypeLabel(fileName: string): string {
    const type = this.getFileType(fileName);
    switch (type) {
      case 'pdf': return 'PDF';
      case 'image': return 'Imagem';
      case 'word': return 'Word';
      default: return 'Arquivo';
    }
  }

  getFileIcon(fileName: string): string {
    const type = this.getFileType(fileName);
    switch (type) {
      case 'pdf': return 'bi-file-earmark-pdf';
      case 'image': return 'bi-file-earmark-image';
      case 'word': return 'bi-file-earmark-word';
      default: return 'bi-file-earmark-text';
    }
  }
}
